/**
 * Decides whether a page load can restore an existing session silently.
 *
 * Extracted from AuthContext so the rules are testable and so the
 * "refresh sends me through SSO again" behaviour can never regress quietly.
 *
 * Guiding rule: a cached account is the proof of sign-in. Everything else
 * (minting an API-scoped token, reaching our own backend) is a secondary
 * concern that may fail transiently and must NOT by itself destroy the
 * session. Only two things justify a fresh interactive sign-in:
 *   - MSAL explicitly reports interaction is required, or
 *   - the backend positively rejects the token (authenticated: false).
 */

const INTERACTION_REQUIRED_CODES = new Set([
  'interaction_required',
  'login_required',
  'consent_required',
  'no_account_error',
]);

const isInteractionRequired = (error) => {
  if (!error) return false;
  const code = error.errorCode || error.name || '';
  if (INTERACTION_REQUIRED_CODES.has(code)) return true;
  if (error instanceof Error && /interaction_required|login_required|consent_required/i.test(error.message || '')) {
    return true;
  }
  return error.constructor?.name === 'InteractionRequiredAuthError';
};

const signedOut = (requiresInteraction) => ({
  isAuthenticated: false,
  requiresInteraction,
  account: null,
  apiToken: null,
  degraded: false,
});

/**
 * @param {object} options
 * @param {object} [options.redirectAccount] account from handleRedirectPromise
 * @param {Array} [options.accounts] accounts already in the MSAL cache
 * @param {Function} options.acquireApiToken async (account) => token|null
 * @param {Function} options.verifyToken async (token) => boolean
 */
const resolveSession = async ({
  redirectAccount = null,
  accounts = [],
  acquireApiToken,
  verifyToken,
}) => {
  const account = redirectAccount || accounts[0] || null;
  if (!account) return signedOut(false);

  let apiToken = null;
  try {
    apiToken = await acquireApiToken(account);
  } catch (error) {
    if (isInteractionRequired(error)) return signedOut(true);
    // Any other failure (network, scope misconfiguration) leaves the session
    // intact but degraded; per-request code retries the token later.
    return { isAuthenticated: true, requiresInteraction: false, account, apiToken: null, degraded: true };
  }

  if (!apiToken) {
    // No token, but no interaction demanded either: stay signed in.
    return { isAuthenticated: true, requiresInteraction: false, account, apiToken: null, degraded: true };
  }

  let verified;
  try {
    verified = await verifyToken(apiToken);
  } catch (error) {
    // Backend unreachable: trust the token we already hold rather than
    // logging the user out over a transient blip.
    return { isAuthenticated: true, requiresInteraction: false, account, apiToken, degraded: true };
  }

  if (verified === false) return signedOut(true);

  return { isAuthenticated: true, requiresInteraction: false, account, apiToken, degraded: false };
};

module.exports = { resolveSession, isInteractionRequired };
